/**
 * Marketing email consent (Warren, 23 October 2026). Pure rules, no I/O, so
 * the send gate (apps/api lib/marketing), the backfill script and the apps
 * read the same definitions.
 *
 * The ruling, in his words: "drivers are enrolled in marketing by default and
 * can opt out", three categories (offers, product updates, company and
 * industry news) all on by default, "Canadian drivers count as enrolled only
 * while they have a paid session within the last 2 years (CASL implied
 * consent)", "at most 2 marketing emails per driver per month", and "anyone
 * who unsubscribed before stays unsubscribed".
 *
 * What lives here:
 *   - the categories, consent bases and record shape stored on
 *     User.marketing_consent;
 *   - evaluateMarketingEligibility, the per-driver decision the send gate
 *     makes at SEND time (so implied consent lapses on its own, no cron);
 *   - the CASL window, the paid-session predicate and the country rule;
 *   - classifyMarketingBackfill, the backfill's per-driver decision;
 *   - the frequency cap constants and the calendar-month key.
 */
// ── Sender identification ────────────────────────────────────────────────────
/**
 * EVI's postal address, exactly as the published legal documents give it
 * (legal/TERMS_OF_SERVICE.md section 25 and the other seven, published
 * 26 September 2026) and as the driver app's push sender identification shows
 * it. Every marketing email carries it (CAN-SPAM and CASL both require a
 * valid postal address).
 */
export const EVI_POSTAL_ADDRESS = 'EV Initiative, Inc., 2108 N St, Suite #4103, Sacramento, CA 95816';
/**
 * The line at the TOP of every marketing email that says what it is
 * (23 October 2026, review). CAN-SPAM (15 U.S.C. 7704(a)(5)(A)(i)) requires a
 * clear and conspicuous identification that a commercial message is an
 * advertisement or solicitation unless the recipient gave affirmative
 * consent. Under the opt-out ruling most drivers are enrolled by default
 * (basis opt_out_default), so that exemption no longer covers them. Every
 * driver and lead marketing email therefore carries this line near the top
 * of the body and of the text part, not only the footer's reason.
 */
export const MARKETING_EMAIL_NOTICE = 'This is a promotional email from EV Initiative.';
/**
 * The consent writer's version, served by the Api at GET /health/capabilities
 * (23 October 2026, review). The backfill refuses --apply unless the stage's
 * live Api reports at least this version: the backfill enrolls drivers in a
 * record only the new Api reads, so applying it under an older Api build
 * would let that build's legacy opt-out toggle write a flag the new send gate
 * never sees.
 */
export const MARKETING_CONSENT_WRITER_VERSION = 1;
/**
 * Whether a global suppression records that the person unsubscribed (23
 * October 2026, review): the email link's unsubscribe, a lead marked
 * unsubscribed in Admin, the backfill's row for an unsubscribed lead, or an
 * admin note that says so. Lifting one mails the person again, so the Api
 * lifts it only with a note of how they consented again.
 */
export function isUnsubscribeSuppressionReason(reason) {
    return /unsubscrib/i.test(reason ?? '');
}
// ── Categories ───────────────────────────────────────────────────────────────
export const MARKETING_CATEGORIES = ['offers', 'product_updates', 'company_news'];
/** English labels for email footers and Admin. The driver app translates its own. */
export const MARKETING_CATEGORY_LABELS = {
    offers: 'Offers',
    product_updates: 'Product updates',
    company_news: 'Company and industry news',
};
export function isMarketingCategory(value) {
    return typeof value === 'string' && MARKETING_CATEGORIES.includes(value);
}
/** Every category on: the default for an enrolled driver. */
export function allMarketingCategories(on) {
    return { offers: on, product_updates: on, company_news: on };
}
// ── Consent record ───────────────────────────────────────────────────────────
/**
 * Why a driver may (or may not) receive marketing email.
 *   opt_out_default        enrolled by default, never opted out, not known to
 *                          be in Canada
 *   express                the driver asked for it (signup box, Settings, the
 *                          preference page)
 *   casl_implied_purchase  Canada: a paid session within 2 years (CASL implied
 *                          consent from a purchase); basis_at is that session
 *   casl_no_basis          Canada with no qualifying paid session: not enrolled
 *   unknown_no_basis       no country evidence at all, and no basis a Canadian
 *                          would need (UNKNOWN_COUNTRY_NEEDS_CASL_BASIS): not
 *                          enrolled until a paid session or a known country
 *   unsubscribed           opted out; stays out until the driver turns a
 *                          category back on themselves
 */
export const MARKETING_CONSENT_BASES = [
    'opt_out_default', 'express', 'casl_implied_purchase', 'casl_no_basis', 'unknown_no_basis', 'unsubscribed',
];
/** How the driver's country was settled, strongest first. v1_station and
 *  v1_operator: the stored v1 place (User.v1_place, Warren 28 Oct 2026),
 *  read only when no v3 fact settles it. */
export const MARKETING_COUNTRY_SOURCES = [
    'home', 'payout', 'session_location', 'session_currency', 'order', 'phone', 'email_domain',
    'v1_station', 'v1_operator', 'unknown',
];
/** Where an unsubscribe came from. The legacy_* values are the backfill's.
 *  'complaint': the person marked a marketing email as spam, reported to EVI
 *  by Amazon SES (23 October 2026), which unsubscribes every driver account
 *  at the address. */
export const MARKETING_UNSUBSCRIBE_SOURCES = [
    'settings', 'email_link', 'one_click', 'signup', 'evii',
    'legacy_opt_out', 'suppression', 'lead_unsubscribe', 'complaint',
];
/**
 * Who last wrote the record. 'backfill' and 'signup' are system defaults the
 * backfill may refresh; the others are the driver's own choices, which the
 * backfill never overwrites.
 */
export const MARKETING_CONSENT_SOURCES = [
    'backfill', 'signup', 'settings', 'email_link', 'one_click', 'evii',
];
export const MARKETING_SYSTEM_SOURCES = ['backfill', 'signup'];
function toDate(v) {
    if (v === null || v === undefined)
        return null;
    const d = v instanceof Date ? v : new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
}
/** Opted out. A record that says unsubscribed in either field is unsubscribed. */
export function isMarketingUnsubscribed(record) {
    if (!record)
        return false;
    return record.basis === 'unsubscribed' || toDate(record.unsubscribed_at ?? null) !== null;
}
/**
 * An opt-out written by an older Api build (23 October 2026, review). Older
 * builds turn marketing off with a plain $set of User.marketing_opt_in false
 * and never touch the record. Under the consent writer that pair cannot
 * arise: an unsubscribe moves the flag and the record together, and the
 * record's source becomes the driver's own (settings, email_link, ...). So a
 * record only the system wrote (backfill or signup) that says the driver
 * opted in (express_at), on a driver whose flag now reads false, is a toggle
 * the older build made after the record was written: the driver turned
 * marketing off. Read it as unsubscribed until the backfill re-run (or the
 * driver's next choice) rewrites the record. An enrolled-by-default record
 * (no express_at) with the flag false is the normal opt-out default, not
 * this.
 */
export function isLegacyOptOutPending(record, legacyOptIn) {
    if (!record || legacyOptIn !== false)
        return false;
    if (!MARKETING_SYSTEM_SOURCES.includes(record.source))
        return false;
    if (isMarketingUnsubscribed(record))
        return false;
    return toDate(record.express_at ?? null) !== null;
}
/** The record a new driver starts with: every category on, enrolled by default. */
export function defaultMarketingConsent(now, source = 'signup') {
    return {
        categories: allMarketingCategories(true),
        basis: 'opt_out_default',
        basis_at: now,
        country: null,
        country_source: null,
        casl_paid_session_at: null,
        express_at: null,
        unsubscribed_at: null,
        unsubscribe_source: null,
        source,
        updated_at: now,
    };
}
/** The record after an unsubscribe from everything. Keeps the country facts. */
export function unsubscribedMarketingConsent(prior, now, how) {
    return {
        categories: allMarketingCategories(false),
        basis: 'unsubscribed',
        basis_at: now,
        country: prior?.country ?? null,
        country_source: prior?.country_source ?? null,
        casl_paid_session_at: prior?.casl_paid_session_at ?? null,
        express_at: prior?.express_at ?? null,
        unsubscribed_at: now,
        unsubscribe_source: how.unsubscribeSource,
        source: how.source,
        updated_at: now,
    };
}
/**
 * A driver's own category change (Settings or the preference page). Turning
 * every category off is an unsubscribe. Turning one on after an unsubscribe is
 * an express re-subscription; the other categories stay as they were (off), so
 * a driver who only wants offers gets only offers.
 */
export function applyMarketingCategoryChange(prior, change, now, how) {
    const base = prior ?? defaultMarketingConsent(now, how.source);
    const wasUnsubscribed = isMarketingUnsubscribed(base);
    const categories = { ...(wasUnsubscribed ? allMarketingCategories(false) : base.categories) };
    let turnedOn = false;
    for (const c of MARKETING_CATEGORIES) {
        const v = change[c];
        if (typeof v !== 'boolean')
            continue;
        if (v && !categories[c])
            turnedOn = true;
        categories[c] = v;
    }
    if (!MARKETING_CATEGORIES.some((c) => categories[c])) {
        return unsubscribedMarketingConsent(base, now, how);
    }
    const express = wasUnsubscribed || turnedOn;
    return {
        ...base,
        categories,
        basis: express ? 'express' : base.basis,
        basis_at: express ? now : base.basis_at,
        express_at: express ? now : (base.express_at ?? null),
        unsubscribed_at: null,
        unsubscribe_source: null,
        source: how.source,
        updated_at: now,
    };
}
/** The driver expressly asked for everything (signup box, legacy toggle on). */
export function expressMarketingConsent(prior, now, source) {
    const base = prior ?? defaultMarketingConsent(now, source);
    return {
        ...base,
        categories: allMarketingCategories(true),
        basis: 'express',
        basis_at: now,
        express_at: now,
        unsubscribed_at: null,
        unsubscribe_source: null,
        source,
        updated_at: now,
    };
}
// ── CASL window and paid sessions ────────────────────────────────────────────
export const CASL_IMPLIED_CONSENT_YEARS = 2;
/** The instant exactly two years before `now` (UTC calendar years). */
export function caslWindowStart(now) {
    const d = new Date(now.getTime());
    d.setUTCFullYear(d.getUTCFullYear() - CASL_IMPLIED_CONSENT_YEARS);
    return d;
}
/** True when a paid session at `paidAt` still carries implied consent at
 *  `now`: strictly after the instant two years before. */
export function withinCaslWindow(paidAt, now) {
    const d = toDate(paidAt ?? null);
    if (!d)
        return false;
    return d.getTime() > caslWindowStart(now).getTime();
}
const V1_PAID = /^(success|succeeded|paid)$/i;
/**
 * A session the driver actually paid for.
 *   v1 history: v1 recorded it paid, or the 23 October clean-up collected it.
 *   v3: billed to the driver (not a fleet) and money moved: captured, wallet
 *   debited, a card charge above zero, or a receipt.
 * A Stripe payment intent id alone is not payment: cancelled holds carry it.
 */
export function isPaidSession(s) {
    if (s.mirrored_from_v1 === true) {
        if (typeof s.v1_cleanup?.payment_intent === 'string' && s.v1_cleanup.payment_intent.length > 0)
            return true;
        return V1_PAID.test((s.v1_payment_status ?? '').trim());
    }
    if (s.billed_to === 'fleet')
        return false;
    return s.payment_status === 'captured'
        || s.wallet_debited === true
        || (s.wallet_debit_cents ?? 0) > 0
        || (s.card_charge_cents ?? 0) > 0
        || (typeof s.stripe_receipt_url === 'string' && s.stripe_receipt_url.length > 0);
}
/** When a session counts as having happened: its end, else its start. */
export function sessionPaidAt(s) {
    return toDate(s.ended_at ?? null) ?? toDate(s.started_at ?? null);
}
/** The latest paid session among `sessions`, or null. */
export function latestPaidSessionAt(sessions) {
    let best = null;
    for (const s of sessions) {
        if (!isPaidSession(s))
            continue;
        const at = sessionPaidAt(s);
        if (at && (!best || at.getTime() > best.getTime()))
            best = at;
    }
    return best;
}
// ── Country ──────────────────────────────────────────────────────────────────
/** Canadian NANP area codes (geographic, plus the non-geographic 600 and 622). */
const CANADIAN_AREA_CODES = new Set([
    '204', '226', '236', '249', '250', '257', '263', '289', '306', '343', '354', '365', '367', '368',
    '382', '387', '403', '416', '418', '428', '431', '437', '438', '450', '460', '468', '474', '506',
    '514', '519', '548', '579', '581', '584', '587', '600', '604', '613', '622', '639', '647', '672',
    '683', '705', '709', '742', '753', '778', '780', '782', '807', '819', '825', '867', '873', '879',
    '902', '905', '942',
]);
/** Country from an E.164 phone: +1 with a Canadian area code is CA, any other
 *  +1 is US. Anything else settles nothing. The weakest signal. */
export function countryFromPhone(phone) {
    const digits = (phone ?? '').replace(/[^\d+]/g, '');
    const m = /^\+1(\d{3})\d{7}$/.exec(digits);
    if (!m)
        return null;
    return CANADIAN_AREA_CODES.has(m[1]) ? 'CA' : 'US';
}
/** Canadian mail providers outside the .ca domain. */
const CANADIAN_MAIL_DOMAINS = new Set(['rogers.com', 'telus.net', 'bell.net', 'sasktel.net', 'mts.net']);
/** Canada from an email address: a .ca domain or a Canadian provider. Only
 *  ever says CA (an American address proves nothing). Weak, but below home
 *  and payout it still outranks any American signal, so that a Canadian who
 *  charged across the border or left a US number is not read as American. */
export function canadaFromEmail(email) {
    const domain = (email ?? '').trim().toLowerCase().split('@')[1] ?? '';
    if (!domain)
        return false;
    return domain.endsWith('.ca') || CANADIAN_MAIL_DOMAINS.has(domain);
}
/**
 * Canadian mail providers by name (Warren, 28 Oct 2026), for the v1 country
 * backfill: a driver at a .ca address or one of these providers is never
 * placed by their v1 operator alone. Matched on any label of the domain but
 * the last, so rogers.com, nb.sympatico.ca and mymts.net match and
 * bellsouth.net does not. Wider than CANADIAN_MAIL_DOMAINS, which the send
 * rule reads through canadaFromEmail.
 */
export const CANADIAN_MAIL_PROVIDERS = new Set([
    'rogers', 'bell', 'bellnet', 'bellaliant', 'aliant', 'sympatico', 'shaw', 'shawcable', 'telus', 'telusplanet',
    'videotron', 'cogeco', 'eastlink', 'sasktel', 'mts', 'mymts', 'primus', 'distributel', 'teksavvy',
    'xplornet', 'execulink', 'tbaytel', 'fido', 'koodo',
]);
/** A .ca address, a CANADIAN_MAIL_DOMAINS address, or one at a Canadian mail provider. */
export function canadianMailbox(email) {
    if (canadaFromEmail(email))
        return true;
    const domain = (email ?? '').trim().toLowerCase().split('@')[1] ?? '';
    const labels = domain.split('.').filter(Boolean);
    return labels.slice(0, -1).some((l) => CANADIAN_MAIL_PROVIDERS.has(l));
}
/** How a stored v1 place was earned (User.v1_place.evidence): the stations
 *  of the driver's v1 bookings, or only their v1 operator. */
export const V1_PLACE_EVIDENCE = ['station', 'operator'];
function normCountry(v) {
    const s = (v ?? '').trim().toUpperCase();
    return /^[A-Z]{2}$/.test(s) ? s : null;
}
/**
 * The driver's country for the CASL rule. Home and payout account first: each
 * is a settled fact about the driver. Below them misreading a Canadian as
 * American is the costly mistake, so ANY remaining Canadian evidence makes it
 * CA (a Canadian location charged at, a CAD session, a store order shipped to
 * Canada, a Canadian area code, a Canadian email domain, a Canadian v1 place,
 * strongest first), and only then the first other location, order or phone
 * country, and last the v1 place. Unknown when nothing settles it.
 */
export function resolveMarketingCountry(e) {
    const home = normCountry(e.home);
    if (home)
        return { country: home, source: 'home' };
    const payout = normCountry(e.payout);
    if (payout)
        return { country: payout, source: 'payout' };
    const locs = (e.sessionCountries ?? []).map(normCountry).filter((c) => c !== null);
    const orders = (e.orderCountries ?? []).map(normCountry).filter((c) => c !== null);
    const phone = countryFromPhone(e.phone);
    if (locs.includes('CA'))
        return { country: 'CA', source: 'session_location' };
    if ((e.sessionCurrencies ?? []).some((c) => (c ?? '').trim().toUpperCase() === 'CAD')) {
        return { country: 'CA', source: 'session_currency' };
    }
    if (orders.includes('CA'))
        return { country: 'CA', source: 'order' };
    if (phone === 'CA')
        return { country: 'CA', source: 'phone' };
    if (canadaFromEmail(e.email))
        return { country: 'CA', source: 'email_domain' };
    const v1 = normCountry(e.v1Place?.country);
    const v1Source = e.v1Place?.evidence === 'operator' ? 'v1_operator' : 'v1_station';
    if (v1 === 'CA')
        return { country: 'CA', source: v1Source };
    if (locs.length > 0)
        return { country: locs[0], source: 'session_location' };
    if (orders.length > 0)
        return { country: orders[0], source: 'order' };
    if (phone)
        return { country: phone, source: 'phone' };
    if (v1)
        return { country: v1, source: v1Source };
    return { country: null, source: 'unknown' };
}
// ── Eligibility at send time ─────────────────────────────────────────────────
/**
 * Canada: express consent does NOT stand in for the purchase. Warren's rule
 * reads "Canadian drivers count as enrolled only while they have a paid
 * session within the last 2 years", so a Canadian who ticked the box but has
 * never paid is not sent marketing. CASL itself would accept express consent
 * gathered with its required disclosures; flipping this constant is the whole
 * change if he rules that way (and the signup copy then needs counsel's eye).
 * The gate then decides from the express date (express_at), which the backfill
 * keeps on every record, so Canadians who ticked the box before the flip are
 * re-admitted even though their stored basis reads casl_no_basis.
 */
export const CASL_EXPRESS_REQUIRES_PURCHASE = true;
/**
 * A driver with no country evidence at all (no home, payout account, session,
 * order, phone or Canadian email domain) may be Canadian, and misreading a
 * Canadian as American is the costly mistake (resolveMarketingCountry). So an
 * unknown country is enrolled only on the basis a Canadian needs: a paid
 * session within 2 years, or express consent once
 * CASL_EXPRESS_REQUIRES_PURCHASE is false. Without one the basis is
 * unknown_no_basis. Read at send time (the gate), by the backfill and by the
 * signup record. False would enroll them by default again (opt_out_default).
 */
export const UNKNOWN_COUNTRY_NEEDS_CASL_BASIS = true;
export const MARKETING_CONSENT_POLICY = Object.freeze({
    caslExpressRequiresPurchase: CASL_EXPRESS_REQUIRES_PURCHASE,
    unknownCountryNeedsCaslBasis: UNKNOWN_COUNTRY_NEEDS_CASL_BASIS,
});
export function marketingRegime(country, policy = MARKETING_CONSENT_POLICY) {
    const c = normCountry(country);
    if (c === 'CA')
        return 'canada';
    if (c === null && policy.unknownCountryNeedsCaslBasis)
        return 'unknown_country';
    return 'opt_out';
}
/**
 * When express consent stands in for a Canadian's purchase: only once
 * CASL_EXPRESS_REQUIRES_PURCHASE is false, and then decided from the express
 * DATE, never the stored basis. The backfill and the basis refresh rewrite a
 * Canadian's basis to casl_implied_purchase or casl_no_basis, so a driver who
 * ticked the signup box keeps that only as express_at. An unsubscribe is
 * excluded before this is asked. Returns the date the consent was given.
 */
export function caslExpressConsentAt(r, policy = MARKETING_CONSENT_POLICY) {
    if (policy.caslExpressRequiresPurchase)
        return null;
    return toDate(r.express_at ?? null);
}
/** The not-enrolled basis of a CASL-style regime. */
function noBasisFor(regime) {
    return regime === 'canada' ? 'casl_no_basis' : 'unknown_no_basis';
}
export const MARKETING_SEND_LOG_BASES = [
    'opt_out_default', 'express', 'casl_implied_purchase', 'lead_audience',
];
/** Under the opt-out rule: express when the driver asked for marketing (the
 *  stored basis, or an express opt-in date on a record whose basis was
 *  Canadian or unknown_no_basis), else enrolled by default. */
function optOutBasis(r) {
    if (r.basis === 'express')
        return 'express';
    if (r.basis === 'opt_out_default')
        return 'opt_out_default';
    return toDate(r.express_at ?? null) ? 'express' : 'opt_out_default';
}
/**
 * The per-driver decision at the moment of sending. Prior unsubscribes win
 * over everything; then the category; then, for Canada and for a driver of
 * unknown country (UNKNOWN_COUNTRY_NEEDS_CASL_BASIS), the 2-year paid session
 * (or express consent, once CASL_EXPRESS_REQUIRES_PURCHASE is false). A
 * driver with no record at all is not sent (fail closed: the backfill and the
 * signup default write one for every driver).
 */
export function evaluateMarketingEligibility(i) {
    const policy = i.policy ?? MARKETING_CONSENT_POLICY;
    const r = i.record;
    if (!r)
        return { eligible: false, reason: 'no_consent_record' };
    if (isMarketingUnsubscribed(r) || isLegacyOptOutPending(r, i.legacyOptIn))
        return { eligible: false, reason: 'unsubscribed' };
    if (r.categories?.[i.category] !== true)
        return { eligible: false, reason: 'category_off' };
    const country = normCountry(i.country);
    const regime = marketingRegime(country, policy);
    if (regime !== 'opt_out') {
        const paid = toDate(i.lastPaidSessionAt ?? null);
        if (paid && withinCaslWindow(paid, i.now)) {
            return { eligible: true, basis: 'casl_implied_purchase', country, casl_paid_session_at: paid };
        }
        if (caslExpressConsentAt(r, policy))
            return { eligible: true, basis: 'express', country, casl_paid_session_at: null };
        return { eligible: false, reason: regime === 'canada' ? 'casl_no_paid_session' : 'unknown_country_no_basis' };
    }
    return {
        eligible: true,
        basis: optOutBasis(r),
        country,
        casl_paid_session_at: null,
    };
}
/**
 * The stored record's basis as the driver's facts stand NOW, or null when the
 * record already says so (or is unsubscribed, which only the driver changes).
 * The send gate decides from the facts at send time either way; this keeps
 * the per-driver record an export can be read from (basis and date, country)
 * from going stale after the backfill: a Canadian signup recorded before the
 * country was known, a casl_no_basis driver who has since paid, a Canadian
 * whose implied consent lapsed.
 *
 *   Canada, and an unknown country (UNKNOWN_COUNTRY_NEEDS_CASL_BASIS)
 *               casl_implied_purchase dated by the latest paid session in the
 *               2-year window; else express dated by express_at once
 *               CASL_EXPRESS_REQUIRES_PURCHASE is false; else casl_no_basis
 *               (Canada) or unknown_no_basis, dated when implied consent
 *               lapsed (two years after the paid session the record held),
 *               else now.
 *   elsewhere   express if the driver asked for it, else opt_out_default.
 *
 * Categories, unsubscribe and the express history are never touched.
 */
export function marketingBasisRefresh(record, facts, now, policy = MARKETING_CONSENT_POLICY) {
    if (!record || isMarketingUnsubscribed(record))
        return null;
    const country = normCountry(facts.country);
    const regime = marketingRegime(country, policy);
    const priorBasisAt = toDate(record.basis_at) ?? now;
    let next;
    if (regime !== 'opt_out') {
        const paid = toDate(facts.lastPaidSessionAt ?? null);
        const expressAt = caslExpressConsentAt(record, policy);
        if (paid && withinCaslWindow(paid, now)) {
            next = { basis: 'casl_implied_purchase', basis_at: paid, country, country_source: facts.country_source, casl_paid_session_at: paid };
        }
        else if (expressAt) {
            next = { basis: 'express', basis_at: expressAt, country, country_source: facts.country_source, casl_paid_session_at: null };
        }
        else {
            const noBasis = noBasisFor(regime);
            let lapsedAt = now;
            const held = toDate(record.casl_paid_session_at ?? null);
            if (record.basis === noBasis) {
                lapsedAt = priorBasisAt;
            }
            else if (record.basis === 'casl_implied_purchase' && held) {
                const end = new Date(held.getTime());
                end.setUTCFullYear(end.getUTCFullYear() + CASL_IMPLIED_CONSENT_YEARS);
                if (end.getTime() <= now.getTime())
                    lapsedAt = end;
            }
            next = { basis: noBasis, basis_at: lapsedAt, country, country_source: facts.country_source, casl_paid_session_at: null };
        }
    }
    else {
        const basis = optOutBasis(record);
        let basisAt = priorBasisAt;
        if (basis !== record.basis)
            basisAt = basis === 'express' ? (toDate(record.express_at ?? null) ?? now) : now;
        next = { basis, basis_at: basisAt, country, country_source: facts.country_source, casl_paid_session_at: null };
    }
    const same = next.basis === record.basis
        && sameInstant(next.basis_at, record.basis_at)
        && next.country === (record.country ?? null)
        && (next.country_source ?? null) === (record.country_source ?? null)
        && sameInstant(next.casl_paid_session_at, record.casl_paid_session_at);
    return same ? null : next;
}
/**
 * A new driver's record at signup, from what the signup knows (Warren,
 * 23 October 2026): every category on, enrolled by default, or express when
 * they ticked the box. The only country evidence a signup has is the phone
 * and the email address. When either says Canada the basis is casl_no_basis
 * from the start (no paid session yet); when neither says anything the
 * country is unknown and the basis is unknown_no_basis
 * (UNKNOWN_COUNTRY_NEEDS_CASL_BASIS). Either way the express opt-in is kept
 * as history (express_at), and counts once CASL_EXPRESS_REQUIRES_PURCHASE is
 * false. So a driver who may be Canadian is never recorded as enrolled by
 * default. The send gate re-reads the full facts at every send, so a new
 * driver who charges at a US site, or pays in Canada, is reached from then on.
 */
export function signupMarketingConsent(now, opts) {
    const policy = opts.policy ?? MARKETING_CONSENT_POLICY;
    const base = opts.express ? expressMarketingConsent(null, now, 'signup') : defaultMarketingConsent(now, 'signup');
    const { country, source } = resolveMarketingCountry({ phone: opts.phone ?? null, email: opts.email ?? null });
    const withCountry = { ...base, country, country_source: source };
    const regime = marketingRegime(country, policy);
    if (regime === 'opt_out' || caslExpressConsentAt(base, policy))
        return withCountry;
    return { ...withCountry, basis: noBasisFor(regime), basis_at: now };
}
// ── Frequency cap ────────────────────────────────────────────────────────────
/** At most this many marketing emails per driver per calendar month (UTC). */
export const MARKETING_MONTHLY_CAP = 2;
/** The calendar month (UTC) a send counts against, as 'YYYY-MM'. */
export function marketingMonthKey(at) {
    return `${at.getUTCFullYear()}-${String(at.getUTCMonth() + 1).padStart(2, '0')}`;
}
function sameInstant(a, b) {
    const x = toDate(a ?? null);
    const y = toDate(b ?? null);
    return (x?.getTime() ?? null) === (y?.getTime() ?? null);
}
/** The fields that make two records the same decision (timestamps of the run excluded). */
export function sameMarketingDecision(a, b) {
    return MARKETING_CATEGORIES.every((c) => a.categories[c] === b.categories[c])
        && a.basis === b.basis
        && sameInstant(a.basis_at, b.basis_at)
        && (a.country ?? null) === (b.country ?? null)
        && (a.country_source ?? null) === (b.country_source ?? null)
        && sameInstant(a.casl_paid_session_at, b.casl_paid_session_at)
        && sameInstant(a.express_at, b.express_at)
        && sameInstant(a.unsubscribed_at, b.unsubscribed_at)
        && (a.unsubscribe_source ?? null) === (b.unsubscribe_source ?? null);
}
/**
 * The backfill's decision for one driver. Idempotent: a re-run over the same
 * facts yields 'unchanged'. Never moves anyone from unsubscribed to enrolled,
 * and never overwrites a choice the driver made themselves.
 *
 * Prior unsubscribes, any one of which keeps the driver out of every category:
 *   - the newest Settings or EVII toggle set marketing_opt_in false;
 *   - they opted in once (reward row, toggle, the flag, or an earlier system
 *     record's express_at) and the flag is now false;
 *   - the address is on the global suppression list;
 *   - a lead row for the address is unsubscribed.
 */
export function classifyMarketingBackfill(f) {
    const existing = f.existing ?? null;
    if (existing && !MARKETING_SYSTEM_SOURCES.includes(existing.source)) {
        return { action: 'skip', reason: 'driver_managed' };
    }
    if (existing && isMarketingUnsubscribed(existing)) {
        return { action: 'skip', reason: 'already_unsubscribed' };
    }
    const policy = f.policy ?? MARKETING_CONSENT_POLICY;
    const country = f.country.country;
    const regime = marketingRegime(country, policy);
    const paidInWindow = f.lastPaidSessionAt && withinCaslWindow(f.lastPaidSessionAt, f.now) ? f.lastPaidSessionAt : null;
    // An earlier system record that says they opted in counts too: with the
    // flag now false, an older Api build turned it off (isLegacyOptOutPending),
    // which the send gate already reads as unsubscribed.
    const everOptedIn = f.marketingOptIn || f.optInRewardAt !== null || f.lastMarketingToggle?.value === true
        || toDate(existing?.express_at ?? null) !== null;
    // The opt-in date when the evidence carries one (toggle row, earlier record, reward row).
    const knownExpressAt = f.marketingOptIn
        ? (f.lastMarketingToggle?.value === true ? f.lastMarketingToggle.at : null)
            ?? toDate(existing?.express_at ?? null) ?? f.optInRewardAt
        : (toDate(existing?.express_at ?? null) ?? null);
    const expressAt = f.marketingOptIn ? (knownExpressAt ?? f.now) : knownExpressAt;
    // A lead unsubscribe or a suppression stands unless the driver opted in
    // AFTER it; an opt-in of unknown date does not outrank it.
    const outranks = (at) => f.marketingOptIn && knownExpressAt !== null && knownExpressAt.getTime() > at.getTime();
    let prior = null;
    if (!f.marketingOptIn && f.lastMarketingToggle?.value === false) {
        prior = { source: 'legacy_opt_out', at: f.lastMarketingToggle.at };
    }
    else if (!f.marketingOptIn && everOptedIn) {
        // Opted in once, off today, and no row dates the change.
        prior = { source: 'legacy_opt_out', at: f.now };
    }
    else if (f.suppressedAt && !outranks(f.suppressedAt)) {
        prior = { source: 'suppression', at: f.suppressedAt };
    }
    else if (f.leadUnsubscribedAt && !outranks(f.leadUnsubscribedAt)) {
        prior = { source: 'lead_unsubscribe', at: f.leadUnsubscribedAt };
    }
    const common = {
        country: country ?? null,
        country_source: f.country.source,
        casl_paid_session_at: regime !== 'opt_out' ? paidInWindow : null,
        express_at: expressAt,
        source: 'backfill',
        updated_at: f.now,
    };
    let record;
    if (prior) {
        record = {
            ...common,
            categories: allMarketingCategories(false),
            basis: 'unsubscribed',
            basis_at: prior.at,
            unsubscribed_at: prior.at,
            unsubscribe_source: prior.source,
        };
    }
    else {
        let basis;
        let basisAt;
        const caslExpressAt = regime !== 'opt_out' ? caslExpressConsentAt({ express_at: expressAt }, policy) : null;
        if (regime !== 'opt_out' && paidInWindow) {
            basis = 'casl_implied_purchase';
            basisAt = paidInWindow;
        }
        else if (caslExpressAt) {
            basis = 'express';
            basisAt = caslExpressAt;
        }
        else if (regime !== 'opt_out') {
            // Canada, or no country evidence at all: not enrolled without a basis.
            basis = noBasisFor(regime);
            basisAt = f.now;
        }
        else if (f.marketingOptIn) {
            basis = 'express';
            basisAt = expressAt ?? f.now;
        }
        else {
            basis = 'opt_out_default';
            basisAt = f.now;
        }
        // A re-run keeps the date the same basis was first recorded.
        if (existing && existing.basis === basis && basis !== 'casl_implied_purchase') {
            basisAt = toDate(existing.basis_at) ?? basisAt;
        }
        record = {
            ...common,
            categories: allMarketingCategories(true),
            basis,
            basis_at: basisAt,
            unsubscribed_at: null,
            unsubscribe_source: null,
        };
    }
    const unchanged = existing !== null && existing.source === 'backfill' && sameMarketingDecision(existing, record);
    return { action: unchanged ? 'unchanged' : 'write', record: unchanged ? existing : record, priorUnsubscribe: prior?.source ?? null };
}
//# sourceMappingURL=marketingConsent.js.map